import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAppData } from '../context/AppDataContext';
import {
  Box, TextField, Button, Typography, Alert, Stack,
  InputAdornment, IconButton, Link, CircularProgress,
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff, LockOutlined, MailOutline } from '@mui/icons-material';
import { authAPI } from '../services/api';
import { BRAND } from '../theme/fitisticTokens';

// ── App theme tokens (matching Flutter app_theme.dart) ────────────────────────
const PRIMARY   = BRAND.primary;
const PRIMARY_DARK = BRAND.primaryDark;
const BG        = BRAND.bg;
const SURFACE   = BRAND.surface;
const TEXT_DARK = BRAND.textDark;
const TEXT_MID  = BRAND.textMid;
const TEXT_LIGHT= BRAND.textLight;
const DIVIDER   = BRAND.divider;

// ── Shared shell ──────────────────────────────────────────────────────────────
export function AuthShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: BG,
        px: 2,
        py: 6,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        {children}
      </Box>
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: TEXT_LIGHT, fontSize: '0.75rem' }}>
          © {new Date().getFullYear()} Fitistic · All rights reserved
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Link
            href="/privacy-policy"
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ fontSize: '0.75rem', color: TEXT_MID, '&:hover': { color: PRIMARY } }}
          >
            Privacy Policy
          </Link>
          <Typography variant="caption" sx={{ color: TEXT_LIGHT }}>·</Typography>
          <Link
            href="/terms"
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ fontSize: '0.75rem', color: TEXT_MID, '&:hover': { color: PRIMARY } }}
          >
            Terms &amp; Conditions
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

// ── Shared brand header ───────────────────────────────────────────────────────
export function AuthBrand() {
  return (
    <Stack alignItems="center" spacing={1.25} sx={{ mb: 3 }}>
      <Box
        sx={{
          width: 56, height: 56,
          borderRadius: '16px',
          bgcolor: '#FFFFFF',
          border: '1.5px solid #E2E8F0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: '0 4px 14px rgba(15,23,42,0.06)',
          p: 0.75,
        }}
      >
        <img
          src="/logo.png" alt="Fitistic"
          style={{ width: 38, height: 38, objectFit: 'contain', display: 'block' }}
          onError={e => {
            e.target.style.display = 'none';
            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
          }}
        />
        <Box
          sx={{
            display: 'none', width: '100%', height: '100%',
            alignItems: 'center', justifyContent: 'center',
            borderRadius: 'inherit',
            bgcolor: '#F5F3FF',
          }}
        >
          <Typography sx={{ color: '#7C3AED', fontWeight: 800, fontSize: 20 }}>F</Typography>
        </Box>
      </Box>
      <Typography fontWeight={800} sx={{ fontSize: '1.25rem', color: TEXT_DARK, letterSpacing: '-0.01em' }}>
        Fitistic
      </Typography>
    </Stack>
  );
}

// ── Shared card ───────────────────────────────────────────────────────────────
export function AuthCard({ title, subtitle, children }) {
  return (
    <>
      <Typography
        variant="h5"
        component="h1"
        fontWeight={800}
        textAlign="center"
        sx={{ mb: 0.75, color: TEXT_DARK, fontSize: '1.5rem', letterSpacing: '-0.02em' }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ mb: 3, color: TEXT_MID, fontSize: '0.875rem', textAlign: 'center' }}>
          {subtitle}
        </Typography>
      )}

      <Box
        sx={{
          bgcolor: SURFACE,
          border: `1px solid ${DIVIDER}`,
          borderRadius: '16px',
          p: { xs: 2.5, sm: 3.5 },
          boxShadow: '0 8px 30px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04)',
        }}
      >
        {children}
      </Box>
    </>
  );
}

// ── Field label row ───────────────────────────────────────────────────────────
function FieldLabel({ children, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
      <Typography
        variant="body2" fontWeight={600}
        sx={{ fontSize: '0.8125rem', color: TEXT_DARK }}
      >
        {children}
      </Typography>
      {action}
    </Stack>
  );
}

// ── Shared text field style ───────────────────────────────────────────────────
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    fontSize: '0.9375rem',
    minHeight: 44,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DIVIDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: PRIMARY,
      borderWidth: '1px',
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px ${SURFACE} inset`,
      WebkitTextFillColor: TEXT_DARK,
      caretColor: TEXT_DARK,
      transition: 'background-color 9999s ease-out 0s',
    },
    '& input:-moz-autofill': {
      boxShadow: `0 0 0 1000px ${SURFACE} inset`,
      WebkitTextFillColor: TEXT_DARK,
      caretColor: TEXT_DARK,
    },
  },
};

// ── Login page ────────────────────────────────────────────────────────────────
export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { reloadGyms, reloadPremium } = useAppData();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      const { token, user } = response.data.data;
      if (!['admin', 'trainer', 'superadmin', 'gym_admin', 'sales_staff', 'coach', 'front_desk', 'manager'].includes(user.role)) {
        setError('Access denied. Admin or staff role required.');
        return;
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      await reloadGyms();
      await reloadPremium();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Incorrect email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <Box sx={{ position: 'absolute', top: 16, right: { xs: 16, sm: 24 } }}>
      <Button
        component={RouterLink}
        to="/"
        startIcon={<ArrowBack />}
        sx={{ mb: 2, color: TEXT_MID, textTransform: 'none', '&:hover': { color: PRIMARY } }}
      >
        Back to main page
      </Button>
      </Box>
      <AuthBrand />

      <AuthCard title="Welcome back 👋" subtitle="Sign in to manage your gym, members and plans.">
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, py: 0.5, fontSize: '0.8125rem' }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {/* Email */}
            <Box>
              <FieldLabel>Email address</FieldLabel>
              <TextField
                required fullWidth autoFocus autoComplete="email"
                type="email" size="small"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                sx={fieldSx}
                inputProps={{ id: 'login-email' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline sx={{ fontSize: 19, color: TEXT_LIGHT }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Password */}
            <Box>
              <FieldLabel
                action={
                  <Link
                    component="button" type="button" underline="none"
                    onClick={() => navigate('/forgot-password')}
                    sx={{
                      fontSize: '0.75rem', color: PRIMARY, fontWeight: 600,
                      '&:hover': { color: PRIMARY_DARK },
                    }}
                  >
                    Forgot password?
                  </Link>
                }
              >
                Password
              </FieldLabel>
              <TextField
                required fullWidth autoComplete="current-password"
                type={showPass ? 'text' : 'password'}
                size="small"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                sx={fieldSx}
                inputProps={{ id: 'login-password' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ fontSize: 19, color: TEXT_LIGHT }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small" edge="end"
                        aria-label={showPass ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPass(v => !v)}
                        sx={{ color: TEXT_LIGHT, '&:hover': { color: TEXT_MID } }}
                      >
                        {showPass
                          ? <VisibilityOff sx={{ fontSize: 17 }} />
                          : <Visibility sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Submit */}
            <Button
              id="login-submit"
              type="submit" fullWidth variant="contained"
              disabled={loading}
              sx={{
                mt: 0.5, py: 1.4,
                fontWeight: 700, fontSize: '0.9375rem',
                borderRadius: '12px',
                bgcolor: PRIMARY,
                boxShadow: '0 4px 16px rgba(124,58,237,0.30)',
                '&:hover': {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: '0 6px 20px rgba(124,58,237,0.40)',
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)' },
                transition: 'all 0.2s',
              }}
            >
              {loading
                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                : 'Sign in'}
            </Button>
          </Stack>
        </Box>
      </AuthCard>

      {/* Role hint */}
      <Box
        sx={{
          mt: 2, p: 1.75,
          bgcolor: BRAND.primarySoft,
          border: `1px solid ${BRAND.primarySoftBorder}`,
          borderRadius: '14px',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: TEXT_MID, fontSize: '0.75rem' }}>
          Access is restricted to{' '}
          <Typography component="span" variant="caption" fontWeight={700} sx={{ color: TEXT_DARK, fontSize: '0.75rem' }}>
            admins and trainers
          </Typography>
          {' '}only.
        </Typography>
      </Box>
    </AuthShell>
  );
}
